import { useCallback, useEffect, useRef, useState } from 'react';

import { NotificationSettingsService } from 'service/NotificationSettingsService';

interface UseDealRequestEmailNotificationsResult {
  isEnabled: boolean;
  isLoading: boolean;
  isSaving: boolean;
  toggle: () => Promise<void>;
}

/**
 * Keeps the "email me about deal requests" flag in sync with the API.
 * Loads the value lazily — only once the chat panel it belongs to is opened.
 */
export function useDealRequestEmailNotifications(shouldLoad: boolean): UseDealRequestEmailNotificationsResult {
  const [isEnabled, setIsEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isLoadedRef = useRef(false);

  useEffect(() => {
    if (!shouldLoad || isLoadedRef.current) return;

    isLoadedRef.current = true;
    setIsLoading(true);

    (async () => {
      try {
        const { data } = await NotificationSettingsService.getSettings();
        setIsEnabled(data.deal_request_email_notifications !== false);
      } catch {
        // Let the user retry on the next panel opening instead of showing a stale value
        isLoadedRef.current = false;
      } finally {
        setIsLoading(false);
      }
    })();
  }, [shouldLoad]);

  const toggle = useCallback(async () => {
    const nextValue = !isEnabled;
    setIsSaving(true);
    setIsEnabled(nextValue);

    try {
      const { data } = await NotificationSettingsService.updateSettings({ deal_request_email_notifications: nextValue });
      setIsEnabled(data.deal_request_email_notifications !== false);
    } catch (error) {
      setIsEnabled(!nextValue);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [isEnabled]);

  return { isEnabled, isLoading, isSaving, toggle };
}
